import { isDate, format, formatRelative as formatRelative$1, formatDistance, formatDuration, parse } from 'date-fns';
import enGB from 'date-fns/locale/en-GB';
import enUS from 'date-fns/locale/en-US';
import esUS from 'date-fns/locale/es';

const DAY_MS = 86400000;
const HOUR_MS = 3600000;
const MINUTE_MS = 60000;
const SECOND_MS = 1000;
const RELATIVE_DATE = {
    "en-US": {
        lastWeek: "MMMM d",
        yesterday: "'Yesterday'",
        today: "'Today'",
        tomorrow: "'Tomorrow'",
        nextWeek: "MMMM d",
        other: "MMMM d"
    },
    "es-US": {
        lastWeek: "MMMM d",
        yesterday: "'Ayer'",
        today: "'Hoy'",
        tomorrow: "'Ma\xf1ana'",
        nextWeek: "MMMM d",
        other: "MMMM d"
    },
    "en-GB": {
        lastWeek: "MMMM d",
        yesterday: "'Yesterday'",
        today: "'Today'",
        tomorrow: "'Tomorrow'",
        nextWeek: "MMMM d",
        other: "MMMM d"
    }
};
const formatRelative = (key, date, lang)=>{
    let format = RELATIVE_DATE[lang][key];
    if ((key === "lastWeek" || key === "nextWeek" || key === "other") && date.getFullYear() !== new Date().getFullYear()) {
        format = `${format}, yyyy`;
    }
    return `${format}`;
};
const formatRelativeTime = (key, date, lang)=>{
    const format = formatRelative(key, date, lang);
    return `${format}, h:mmaa`;
};
const LOCALES = {
    "en-US": {
        ...enUS,
        formatRelative: (key, date)=>formatRelative(key, date, "en-US"),
        formatDistance: (token, count)=>{
            const formatDistanceLocale = {
                xSeconds: {
                    one: "1 sec",
                    other: "{{count}} sec"
                },
                xMinutes: {
                    one: "1 min",
                    other: "{{count}} min"
                },
                xHours: {
                    one: "1 hr",
                    other: "{{count}} hr"
                },
                xDays: {
                    one: "1 day",
                    other: "{{count}} days"
                }
            };
            if (count === 1) {
                return formatDistanceLocale[token].one;
            } else {
                return formatDistanceLocale[token].other.replace("{{count}}", count.toString());
            }
        }
    },
    "es-US": {
        ...esUS,
        formatRelative: (key, date)=>formatRelative(key, date, "es-US")
    },
    "en-GB": {
        ...enGB,
        formatRelative: (key, date)=>formatRelative(key, date, "en-US"),
        formatDistance: (token, count)=>{
            const formatDistanceLocale = {
                xSeconds: {
                    one: "1 sec",
                    other: "{{count}} sec"
                },
                xMinutes: {
                    one: "1 min",
                    other: "{{count}} min"
                },
                xHours: {
                    one: "1 hr",
                    other: "{{count}} hr"
                },
                xDays: {
                    one: "1 day",
                    other: "{{count}} days"
                }
            };
            if (count === 1) {
                return formatDistanceLocale[token].one;
            } else {
                return formatDistanceLocale[token].other.replace("{{count}}", count.toString());
            }
        }
    }
};
const LOCALES_TIME = {
    "en-US": {
        ...enUS,
        formatRelative: (key, date)=>formatRelativeTime(key, date, "en-US")
    },
    "es-US": {
        ...esUS,
        formatRelative: (key, date)=>formatRelativeTime(key, date, "es-US")
    },
    "en-GB": {
        ...enGB,
        formatRelative: (key, date)=>formatRelativeTime(key, date, "en-GB")
    }
};
const getLangLocale = (lang)=>{
    let locale = "en-US";
    if (lang === "en-GB" || lang === "en-AU") {
        locale = "en-GB";
    }
    if (lang === "es" || lang === "es-US") {
        locale = "es-US";
    }
    return locale;
};
const i18nFormat = (value, format$1, lng)=>{
    if (isDate(value)) {
        const lang = getLangLocale(lng);
        const locale = LOCALES[lang];
        if (format$1 === "short") {
            if (value.getFullYear() !== new Date().getFullYear()) {
                return format(value, "MMM d, yyyy", {
                    locale
                });
            }
            return format(value, "MMM d", {
                locale
            });
        }
        if (format$1 === "long") {
            return format(value, "PPPP", {
                locale
            });
        }
        if (format$1 === "datetime") {
            if (value.getFullYear() !== new Date().getFullYear()) {
                return format(value, "MMM d, yyyy, h:mmaa", {
                    locale
                });
            }
            return format(value, "MMM d, h:mmaa", {
                locale
            });
        }
        if (format$1 === "day") {
            return format(value, "d", {
                locale
            });
        }
        if (format$1 === "relativemonth") {
            if (value.getFullYear() !== new Date().getFullYear()) {
                return format(value, "MMM yyyy", {
                    locale
                });
            }
            return format(value, "MMM", {
                locale
            });
        }
        if (format$1 === "relative") {
            return formatRelative$1(value, new Date(), {
                locale,
                weekStartsOn: WEEK_STARTS_ON[lang]
            });
        }
        if (format$1 === "relativetime") {
            const localeTime = LOCALES_TIME[lang];
            return formatRelative$1(value, new Date(), {
                locale: localeTime,
                weekStartsOn: WEEK_STARTS_ON[lang]
            });
        }
        if (format$1 === "ago") {
            return formatDistance(value, new Date(), {
                locale,
                addSuffix: true
            });
        }
        if (format$1 === "daymonth") {
            return value.toLocaleDateString(lang, {
                month: "numeric",
                day: "numeric"
            });
        }
        return format(value, format$1 ?? "", {
            locale
        });
    }
    if (typeof value === "number" && format$1 === "duration") {
        const lang1 = getLangLocale(lng);
        const locale1 = LOCALES[lang1];
        const days = Math.floor(value / DAY_MS);
        const hours = Math.floor(value % DAY_MS / HOUR_MS);
        const minutes = Math.floor(value % HOUR_MS / MINUTE_MS);
        const seconds = Math.floor(value % MINUTE_MS / SECOND_MS);
        if (days >= 1) {
            return formatDuration({
                days,
                hours
            }, {
                delimiter: ", ",
                locale: locale1
            });
        }
        if (hours >= 1) {
            return formatDuration({
                hours,
                minutes
            }, {
                delimiter: ", ",
                locale: locale1
            });
        }
        return formatDuration({
            minutes,
            seconds
        }, {
            delimiter: ", ",
            locale: locale1
        });
    }
    return value;
};
const formatDateUtil = (date, format$1 = "PPPP", lang = "en-US")=>{
    return format(date, Array.isArray(format$1) ? format$1[0] : format$1, {
        locale: LOCALES[lang]
    });
};
const parseDate = (value, format = "PPPP", lang = "en-US")=>{
    return parse(value, Array.isArray(format) ? format[0] : format, new Date(), {
        locale: LOCALES[lang]
    });
};
const formatDay = (date, lang = "en-US")=>{
    return format(date, "PPPP", {
        locale: LOCALES[lang]
    });
};
const formatMonthTitle = (date, lang = "en-US")=>{
    return format(date, "LLLL Y", {
        locale: LOCALES[lang]
    });
};
const WEEKDAYS_SHORT = {
    "en-US": [
        enUS.localize?.day(0, {
            width: "short"
        }),
        enUS.localize?.day(1, {
            width: "short"
        }),
        enUS.localize?.day(2, {
            width: "short"
        }),
        enUS.localize?.day(3, {
            width: "short"
        }),
        enUS.localize?.day(4, {
            width: "short"
        }),
        enUS.localize?.day(5, {
            width: "short"
        }),
        enUS.localize?.day(6, {
            width: "short"
        })
    ],
    "es-US": [
        esUS.localize?.day(0, {
            width: "short"
        }),
        esUS.localize?.day(0, {
            width: "short"
        }),
        esUS.localize?.day(1, {
            width: "short"
        }),
        esUS.localize?.day(2, {
            width: "short"
        }),
        esUS.localize?.day(3, {
            width: "short"
        }),
        esUS.localize?.day(4, {
            width: "short"
        }),
        esUS.localize?.day(5, {
            width: "short"
        }),
        esUS.localize?.day(6, {
            width: "short"
        })
    ],
    "en-GB": [
        enGB.localize?.day(0, {
            width: "short"
        }),
        enGB.localize?.day(1, {
            width: "short"
        }),
        enGB.localize?.day(2, {
            width: "short"
        }),
        enGB.localize?.day(3, {
            width: "short"
        }),
        enGB.localize?.day(4, {
            width: "short"
        }),
        enGB.localize?.day(5, {
            width: "short"
        }),
        enGB.localize?.day(6, {
            width: "short"
        })
    ]
};
const formatWeekdayShort = (index = 0, lang = "en-US")=>{
    return WEEKDAYS_SHORT[lang][index];
};
const formatWeekdayLong = (index = 0, lang = "en-US")=>{
    return WEEKDAYS_SHORT[lang][index];
};
const WEEK_STARTS_ON = {
    "en-US": enUS.options?.weekStartsOn ?? 0,
    "es-US": esUS.options?.weekStartsOn ?? 0,
    "en-GB": enGB.options?.weekStartsOn ?? 0
};
const getFirstDayOfWeek = (lang)=>{
    return WEEK_STARTS_ON[lang];
};
const MONTHS = {
    "en-US": [
        enUS.localize?.month(0),
        enUS.localize?.month(1),
        enUS.localize?.month(2),
        enUS.localize?.month(3),
        enUS.localize?.month(4),
        enUS.localize?.month(5),
        enUS.localize?.month(6),
        enUS.localize?.month(7),
        enUS.localize?.month(8),
        enUS.localize?.month(9),
        enUS.localize?.month(10),
        enUS.localize?.month(11)
    ],
    "es-US": [
        esUS.localize?.month(0),
        esUS.localize?.month(1),
        esUS.localize?.month(2),
        esUS.localize?.month(3),
        esUS.localize?.month(4),
        esUS.localize?.month(5),
        esUS.localize?.month(6),
        esUS.localize?.month(7),
        esUS.localize?.month(8),
        esUS.localize?.month(9),
        esUS.localize?.month(10),
        esUS.localize?.month(11)
    ],
    "en-GB": [
        enGB.localize?.month(0),
        enGB.localize?.month(1),
        enGB.localize?.month(2),
        enGB.localize?.month(3),
        enGB.localize?.month(4),
        enGB.localize?.month(5),
        enGB.localize?.month(6),
        enGB.localize?.month(7),
        enGB.localize?.month(8),
        enGB.localize?.month(9),
        enGB.localize?.month(10),
        enGB.localize?.month(11)
    ]
};
const getMonths = (lang)=>{
    return MONTHS[lang];
};
const i18nPickerUtils = {
    formatDate: formatDateUtil,
    formatDay,
    formatMonthTitle,
    formatWeekdayShort,
    formatWeekdayLong,
    getFirstDayOfWeek,
    getMonths,
    parseDate
};

export { i18nFormat, i18nPickerUtils };
